import { map } from 'lodash';
import { v4 as uuid } from 'uuid';
import { epoch, formatEpochAsDate, formatEpochAsTime, utc } from '../../util/datetime';
import { toUUID } from '../../util/transform';
import Address from '../address/Address';
import { AssetType } from './Asset.enum';
import { BankAccountType } from '../global/BankAccountType.enum';
import { PropertyType } from '../global/PropertyType.enum';
import { VehicleType } from '../global/VehicleType.enum';
import { ZoningOption } from '../global/ZoningOption.enum';
import type { IAsset } from './Asset.interface';
import Ownership from './Ownership';
import PropertyDetails from './PropertyDetails';

export default class Asset implements IAsset {
  id: string;
  idReference: string;
  assetType: AssetType;
  name: string;
  address: Address;
  value: number;
  monthlyRentalIncome: number;
  numberOfBoarders: number;
  vehicleType: VehicleType;
  vehicleBuildDate: number;
  bankOtherName: string;
  bankAccountType: BankAccountType;
  bankBsb: string;
  bankAccountNumber: string;
  membershipNumber: string;
  propertyType: PropertyType;
  zoning: ZoningOption;
  asAtDate: number;
  ownership: Array<Ownership>;
  propertyDetails: PropertyDetails;
  toBeSold: boolean;
  valuation: boolean;
  willItBecomeOwnerOccupied: boolean;
  willItBecomeInvestment: boolean;
  allInclusive: boolean;
  directDebit: boolean;
  timeStamp: number;
  isActive: boolean;

  hasAddress() {
    return Address.hasAddress(this?.address);
  }

  getName() {
    if (
      (Asset.getAvailableAssetTypes()[0].id === this.assetType.id ||
        Asset.getAvailableAssetTypes()[1].id === this.assetType.id) &&
      this.hasAddress()
    ) {
      return Address.getAddress(this?.address);
    }
    return this?.name;
  }

  getAssetTypeName() {
    return this?.assetType?.name || '';
  }

  getSetVehicleBuildDate(timeZone, locale, localeFormat) {
    return formatEpochAsDate(this.vehicleBuildDate, timeZone, locale, localeFormat);
  }

  getSetAsAtDate(timeZone, locale, localeFormat) {
    return formatEpochAsDate(this.asAtDate, timeZone, locale, localeFormat);
  }

  getDate(timeZone, locale, localeFormat) {
    return formatEpochAsDate(this.timeStamp, timeZone, locale, localeFormat);
  }

  getTime(timeZone, locale, localeFormat) {
    return formatEpochAsTime(this.timeStamp, timeZone, locale, localeFormat);
  }

  static getAvailableAssetTypes = () => AssetType.available();

  static build = (data: IAsset) => new Asset(data);
  static blank = (assetType: AssetType, ownership: Ownership) =>
    new Asset({ id: uuid(), assetType, ownership, isActive: true });

  /**
   * Constructor
   * @param {IAsset} data
   */
  constructor(data?: IAsset) {
    this.id = toUUID(data?.id) || uuid();
    // TODO: idTicketAsset is legacy structure
    this.idReference = toUUID(data?.idReference || data?.idTicketAsset);
    this.assetType = AssetType.of(data?.assetType?.id || data?.assetType?.name);
    this.name = data?.name || '';
    this.address = new Address(data?.address);
    this.value = parseFloat(data?.value || 0);
    this.monthlyRentalIncome = parseFloat(data?.monthlyRentalIncome || 0);
    this.numberOfBoarders = parseInt(data?.numberOfBoarders || 0, 10);
    this.vehicleType = VehicleType.of(data?.vehicleType?.name || data?.vehicleType?.toString());
    this.vehicleBuildDate = data?.vehicleBuildDate ? epoch(utc(data?.vehicleBuildDate)) : 0;
    this.bankAccountType = BankAccountType.of(
      data?.bankAccountType?.name || data?.bankAccountType?.toString()
    );
    this.bankBsb = data?.bankBsb || '';
    this.bankOtherName = data?.bankOtherName || '';
    this.bankAccountNumber = data?.bankAccountNumber || '';
    this.membershipNumber = data?.membershipNumber || '';
    this.propertyType = PropertyType.of(
      data?.propertyType?.name || data?.propertyType?.toString()
    );
    this.zoning = ZoningOption.of(data?.zoning?.name || data?.zoning?.toString());
    this.asAtDate = data?.asAtDate ? epoch(utc(data?.asAtDate)) : 0;
    this.ownership = map(data?.ownership, (obj) => new Ownership(obj));
    this.propertyDetails = new PropertyDetails(data?.propertyDetails);
    this.toBeSold = data?.toBeSold === true;
    this.valuation = data?.valuation === true;
    this.willItBecomeOwnerOccupied = data?.willItBecomeOwnerOccupied === true;
    this.willItBecomeInvestment = data?.willItBecomeInvestment === true;
    this.allInclusive = data?.allInclusive === true;
    this.directDebit = data?.directDebit === true;
    this.timeStamp = epoch(utc(data?.timeStamp));
    this.isActive = data?.isActive === true;
  }
}
